import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import { useMemo } from "react";
import PropTypes from "prop-types";
import { Checkbox, Form } from "semantic-ui-react";
import { Hotels, OverBooking, RoomTypes, Wings } from "../../api/services/next";
import { dateFormat } from "../../components/shared/constants";
import HotelsDropDown from "../../components/shared/HotelsDropDown";
import { onError, onWarning } from "../../components/shared/messages";
import RoomTypesDropDown from "../../components/shared/RoomTypesDropDown";
import WingDropDown from "../../components/shared/WingDropDown";
import UpdateSourceDropdown from "../../components/shared/UpdateSourceDropdown";
import AllowRMSDecisionStatusDropdown from "../../components/shared/AllowRMSDecisionStatusDropdown";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { useMessageResources, useTextResources } from "../../language";
import GridSearchTemplate from '../GridSearchTemplate';
import CheckBoxToggle from "../../components/shared/CheckBoxToggle";
import { OverbookingCreationPopup } from "./OverbookingCreationPopup";
import "./style.css"
import { DatePicker } from "../../components/shared/DatePicker";
import { useLoader } from "../../hooks/useLoader";

export const OverBookingPage = () => {
    const currentHotel = useCurrentHotel();
    const [search, setSearch] = useState({
        FromDate: moment().startOf("day").toDate(),
        ToDate: moment().add(30, 'days').startOf("day").toDate(),
        IsSuggested: false,
        HotelIds: currentHotel ? [currentHotel?.Id] : []
    })
    const [hotels, setHotels] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [wings, setWings] = useState([]);
    const [UpdateOverbookingPopupOpen, setUpdateOverbookingPopupOpen] = useState(false);
    const doSearch = useRef();
    const message = useMessageResources();
    const loader = useLoader();
    const text = useTextResources();

    useEffect(() => {
        (async () => {
            try {
                loader(true);
                let fetchedHotels = await Hotels.GetHotels({ HotelChainId: currentHotel?.HotelChainId });
                let fetchedRoomTypes = await RoomTypes.GetRoomTypes({ HotelIds: fetchedHotels.map(hotel => hotel.Id) });
                let fetchedWings = await Wings.GetWings({ HotelIds: fetchedHotels.map(hotel => hotel.Id) });

                setHotels(fetchedHotels);
                setRoomTypes(fetchedRoomTypes);
                setWings(fetchedWings);
            } catch (e) {
                onError(e);
            } finally {
                loader(false);
            }
        })();
    }, [])

    const roomTypeSpec = useMemo(() => ({ HotelIds: search.HotelIds }), [search.HotelIds]);
    const wingSpec = useMemo(() => ({ HotelIds: search.HotelIds }), [search.HotelIds]);

    const addRow = () => {
        doSearch.current?.();
    }

    const formatDate = ({ value }) => {
        return value ? moment(value).format(dateFormat) : "";
    };

    const ExternalUpdateFormatter = ({ value, row }) => {
        if (row)
            return <Checkbox checked={value !== false} />
        else return value === false ? text("DontAllow") : value ? text("Allow") : `${text("Empty")} (${text("Allow")})`;
    }

    const ByRmsFormatter = ({ value }) => <Checkbox checked={value !== undefined} />;

    const filterEntitiesByName = (entities, Id = "", value) => {
        if (value.trim() === "") { 
            return true;
        }

        const currentEntity = entities.find(entity => entity.Id === Id);
        if (!currentEntity || !currentEntity.Name) { 
            return false;
        }

        return currentEntity.Name.toLowerCase().trim().includes(value.toLowerCase().trim());
    }

    const validations = spec => {
        if (spec.FromDate > spec.ToDate) {
            onWarning(message("ToDateGreaterThenFromDate"))
            return false
        }
        return true
    }

    const onSearchChange = spec => {
        setSearch(prev => ({
            ...prev,
            ...spec
        }))
    }

    const gridOptions = {
        search: {
            title: text("Overbooking"),
            fields: [
                <Form.Field label={text('Hotels')} placeholder={text('All')} multiple control={HotelsDropDown} key="HotelIds" clearable />,
                <DatePicker clearable label={text('StartDate')} key='FromDate' />,
                <DatePicker clearable label={text('EndDate')} key='ToDate' />,
                <Form.Field label={text('Wings')} spec={wingSpec} placeholder={text('All')} multiple control={WingDropDown} key="WingIds" clearable />,
                <Form.Field label={text('RoomTypes')} spec={roomTypeSpec} placeholder={text('All')} multiple control={RoomTypesDropDown} key="RoomTypeIds" clearable />,
                <Form.Field label={text('UpdateSource')} control={UpdateSourceDropdown} key="IsUpdatedByRms" />,
                <Form.Field label={text('AllowRMSDecisionStatus')} control={AllowRMSDecisionStatusDropdown} key="AllowExternalUpdate" />
            ],
            initial: search,
            spec: search,
            validate: validations,
            method: 'POST',
            api: OverBooking.GetOverBooking,
            setDoSearchRef: ref => doSearch.current = ref,
            searchOnLoad: true,
        },
        grid: {
            columns: [
                { name: 'HotelId', title: text("Hotel"), formatter: ({ value }) => hotels.find(hotel => hotel.Id === value)?.Name || "" },
                { name: 'RoomTypeId', title: text("RoomType"), formatter: ({ value }) => roomTypes.find(hotel => hotel.Id === value)?.Name || "" },
                { name: 'WingId', title: text("Wing"), formatter: ({ value }) => wings.find(hotel => hotel.Id === value)?.Name || "" },
                { name: 'Date', title: text("Date"), formatter: formatDate },
                { name: 'MaxOverbookedRoomQuantity', title: text("MaxOverbookedQuantity") },
                { name: 'AllowExternalUpdate', title: text("AllowRmsDecisions"), formatter: ExternalUpdateFormatter, customFilter: CheckBoxToggle, },
                { name: 'RmsUpdateId', title: text("ByRms"), formatter: ByRmsFormatter, customFilter: CheckBoxToggle, },

            ],
            filtering: {
                extensions: [
                    { columnName: "RmsUpdateId", predicate: (rmsUpdateId, { value }) => value === false ? rmsUpdateId === undefined : rmsUpdateId !== undefined },
                    { columnName: "AllowExternalUpdate", predicate: (allow, { value }) => value === false ? allow === false : allow !== false },
                    { columnName: 'HotelId', predicate: (hotelId, { value }) => filterEntitiesByName(hotels, hotelId, value) },
                    { columnName: 'RoomTypeId', predicate: (roomTypeId, { value }) => filterEntitiesByName(roomTypes, roomTypeId, value) },
                    { columnName: 'WingId', predicate: (wingId, { value }) => filterEntitiesByName(wings, wingId, value) },
                    { columnName: 'Date', predicate: (date, { value }) => formatDate({value: date}).includes(value.toLowerCase().trim()) }
                ],
            },
            cacheId: 'OverBookingPage',
            customOptions: {
                group: {},
                sort: {}
            },
            paging: true,
            editable: {
                allowAdd: true,
                modalEditor: {
                    modal: OverbookingCreationPopup,
                    props: {
                        addRow: addRow,
                    }
                }
            }
        }
    }

    return <div className="gridSearchTemplate">
        <GridSearchTemplate
            options={gridOptions} onSearchChange={onSearchChange} />
        <OverbookingCreationPopup isUpdate open={UpdateOverbookingPopupOpen} onClose={() => setUpdateOverbookingPopupOpen(false)} />
    </div>
};

OverBookingPage.propTypes = {
    value: PropTypes.number,
    row: PropTypes.object
};